import { useState } from 'react';
import { useBulkActionsState } from 'src/components/common/BulkActions/useBulkActionsState';
import { useBulkAdjustCost } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkAdjustCost';
import { useBulkCancelPurchase } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkCancelPurchase';
import { useBulkDeleteTicketGroup } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkDeleteTicketGroup';
import { useBulkEditCommissionAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkEditCommissionAction';
import { useBulkUpdateDeliveryType } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateDeliveryType';
import { useBulkUpdateInternalNotesAction } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateInternalNotesAction';
import { useBulkUpdatePurchaseCurrencyCode } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseCurrencyCodeAction';
import { useBulkUpdatePurchaseSettings } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseGeneralSettings';
import { useBulkUpdateSeatTraits } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdateSeatTraits';
import { useExportPurchasesAction } from 'src/components/Purchases/PurchaseActionDropdown/components/LaunchExportPurchasesAction/useExportPurchasesAction';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { ChevronDownIcon, IconsFill } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Permission } from 'src/WebApiController';

import { usePurchaseTileBulkActionsState } from '../../PurchasesTileBulkActions/usePurchaseTileBulkActionsState';
import { useUpdatePurchasesPermissions } from '../../useUpdatePurchasesPermissions';
import { PurchasesBulkActionProps } from '../PurchasesBulkActions.types';

export const SettingsActions = ({ isEventView }: PurchasesBulkActionProps) => {
  const { areBulkActionsEnabled } = useBulkActionsState();
  const hasPurchasesExportPermission = useUserHasAnyOfPermissions(
    Permission.Purchases_Export
  );
  const [isLoading, setIsLoading] = useState(false);
  const { filterQueryWithEventIds } = usePurchaseTileBulkActionsState(
    undefined,
    isEventView
  );

  const bulkUpdateGeneralSettings = useBulkUpdatePurchaseSettings(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdateDeliveryType = useBulkUpdateDeliveryType(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdateSeatTraits = useBulkUpdateSeatTraits(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdateInternalNotes = useBulkUpdateInternalNotesAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkEditCommissionAction = useBulkEditCommissionAction(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkCancelPurchaseAction = useBulkCancelPurchase(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkDeleteTicketGroupAction = useBulkDeleteTicketGroup(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkUpdatePurchaseCurrencyCode = useBulkUpdatePurchaseCurrencyCode(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const bulkAdjustCost = useBulkAdjustCost(
    filterQueryWithEventIds,
    setIsLoading,
    isLoading,
    undefined,
    isEventView
  );

  const exportPurchasesAction = useExportPurchasesAction({
    style: { color: vars.color.textStrong, width: '100%' },
    variant: 'text',
    shape: 'rect',
    filterQuery: filterQueryWithEventIds,
    disabled: !areBulkActionsEnabled,
    textContentId: ContentId.ExportPurchases,
    textOnly: true,
    isEventView: isEventView ?? false,
  });

  const {
    hasUpdatePurchasesPermission,
    hasPurchaseUpdateCommissionPermission,
  } = useUpdatePurchasesPermissions();

  if (!hasUpdatePurchasesPermission && !hasPurchaseUpdateCommissionPermission) {
    return null;
  }

  return (
    <>
      <PosDropdown
        key="purchases-settings-actions-dropdown"
        trigger={
          <Button variant="outline">
            <Content id={ContentId.Settings} />
            <ChevronDownIcon
              size={vars.iconSize.xs}
              fill={IconsFill.currentColor}
            />
          </Button>
        }
        align="start"
        triggerProps={{ disabled: isLoading || !areBulkActionsEnabled }}
      >
        {!isEventView && bulkUpdateGeneralSettings.dropDown}

        {bulkUpdateDeliveryType.dropDown}
        {bulkUpdateSeatTraits.dropDown}

        {!isEventView && bulkUpdateInternalNotes.dropDown}
        {!isEventView && bulkEditCommissionAction.dropDown}
        {!isEventView && bulkCancelPurchaseAction.dropDown}

        {isEventView && bulkDeleteTicketGroupAction.dropDown}

        {hasPurchasesExportPermission && exportPurchasesAction.dropdown}

        {!isEventView && bulkUpdatePurchaseCurrencyCode.dropDown}

        {bulkAdjustCost.dropDown}
      </PosDropdown>
    </>
  );
};
